<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
import type { Shortcut } from '@/composables/useKeyboardShortcuts'
import { Keyboard } from 'lucide-vue-next'

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { allShortcuts } = useKeyboardShortcuts()

const isMac = navigator.platform.toUpperCase().includes('MAC')
const modifierSymbol = isMac ? '\u2318' : 'Ctrl'

interface GroupedShortcuts {
  category: string
  shortcuts: Shortcut[]
}

const grouped = computed((): GroupedShortcuts[] => {
  const map = new Map<string, Shortcut[]>()
  for (const s of allShortcuts.value) {
    const cat = s.category || 'General'
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(s)
  }
  const result: GroupedShortcuts[] = []
  for (const [category, shortcuts] of map) {
    result.push({ category, shortcuts })
  }
  return result
})

function formatKey(shortcut: Shortcut): string[][] {
  // Returns an array of key-combo groups, each group is an array of individual keys
  const id = shortcut.id

  // Sequence shortcuts (e.g., "g t")
  if (id.includes(' ')) {
    const parts = id.split(' ')
    return parts.map(part => formatSingleCombo(part))
  }

  return [formatSingleCombo(id)]
}

function formatSingleCombo(id: string): string[] {
  const parts = id.split('+')
  return parts.map(part => {
    if (part === 'ctrl') return modifierSymbol
    if (part === 'shift') return 'Shift'
    if (part === 'alt') return isMac ? '\u2325' : 'Alt'
    if (part === '?') return '?'
    return part.toUpperCase()
  })
}
</script>

<template>
  <BaseModal :show="show" title="Keyboard Shortcuts" size="md" @close="emit('close')">
    <template #header>
      <div class="modal-header">
        <div class="flex items-center gap-2">
          <Keyboard class="w-5 h-5 text-primary-400" />
          <h2 class="text-lg font-semibold text-surface-100">Keyboard Shortcuts</h2>
        </div>
        <button
          @click="emit('close')"
          class="p-1 text-surface-400 hover:text-surface-100 rounded transition-colors"
        >
          <span class="sr-only">Close</span>
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </template>

    <div class="space-y-6">
      <div v-for="group in grouped" :key="group.category">
        <h3 class="text-xs font-semibold uppercase tracking-wider text-surface-500 mb-3">
          {{ group.category }}
        </h3>
        <div class="space-y-2">
          <div
            v-for="shortcut in group.shortcuts"
            :key="shortcut.id"
            class="flex items-center justify-between py-1.5"
          >
            <span class="text-sm text-surface-300">{{ shortcut.description }}</span>
            <div class="flex items-center gap-1.5 flex-shrink-0 ml-4">
              <template v-for="(combo, ci) in formatKey(shortcut)" :key="ci">
                <span
                  v-if="ci > 0"
                  class="text-xs text-surface-500 mx-0.5"
                >then</span>
                <template v-for="(k, ki) in combo" :key="ki">
                  <span
                    v-if="ki > 0"
                    class="text-xs text-surface-500"
                  >+</span>
                  <kbd class="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-medium text-surface-200 bg-surface-700 border border-surface-600 rounded shadow-sm">
                    {{ k }}
                  </kbd>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="allShortcuts.length === 0" class="text-center py-8 text-surface-500 text-sm">
        No shortcuts registered.
      </div>
    </div>
  </BaseModal>
</template>
